<template>
  <div class="container">
    <div class="form__wrapper">
      <div class="form__title">Learner Registration</div>
      <div class="form__body">
        <div v-if="getErrorMessage" class="alert alert-danger" role="alert">{{getErrorMessage}}</div>
        <form @submit.prevent="registerUser">
          <div class="form-row">
            <div class="col-md-6">
              <div class="form-group">
                <label for="fname">First Name</label>
                <input
                  id="fname"
                  v-model="itemData.fname"
                  class="form-control"
                  :class="[{'is-invalid': errors.fname}]"
                  @keydown="clearErrors($event.target.name)"
                  type="text"
                  placeholder="First Name"
                >
                <div class="invalid-feedback">{{ errors.fname }}</div>
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <label for="lname">Last Name</label>
                <input
                  id="lname"
                  class="form-control"
                  v-model="itemData.lname"
                  :class="[{'is-invalid': errors.lname}]"
                  @keydown="clearErrors($event.target.name)"
                  type="text"
                  placeholder="Last Name"
                >
                <div class="invalid-feedback">{{ errors.lname }}</div>
              </div>
            </div>
          </div>

          <div class="form-row">
            <div class="col-md-12">
              <div class="form-group">
                <label for="email">Email Address</label>
                <input
                  id="email"
                  class="form-control"
                  placeholder="Email Address"
                  type="email"
                  v-model="itemData.email"
                  :class="[{'is-invalid': errors.email}]"
                  @keydown="clearErrors($event.target.name)"
                >
                <div class="invalid-feedback">{{ errors.email }}</div>
              </div>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group col-md-6">
              <label for="password">Password</label>
              <input
                id="password"
                class="form-control"
                v-model="itemData.password"
                :class="[{'is-invalid': errors.password}]"
                @keydown="clearErrors($event.target.name)"
                type="password"
                placeholder="********"
              >
              <div class="invalid-feedback">{{ errors.password }}</div>
            </div>
            <div class="form-group col-md-6">
              <label for="phone">Phone Number</label>
              <input
                id="phone"
                v-model="itemData.phone"
                :class="[{'is-invalid': errors.phone}]"
                @keydown="clearErrors($event.target.name)"
                type="text"
                class="form-control"
                placeholder="Phone Number"
              >
              <div class="invalid-feedback">{{ errors.phone }}</div>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group col-md-6">
              <label for="category">State</label>
              <select
                v-model="state_id"
                class="custom-select form__select"
                :class="[{ 'is-invalid': errors.state_id}]"
                id="state"
              >
                <option disabled value>Select State</option>
                <template v-if="statesLoaded && states.length">
                  <option v-for="state in states" :value="state.id" :key="state.id">{{ state.name }}</option>
                </template>
                <template v-else>
                  <option>Error loading states</option>
                </template>
              </select>
              <div class="invalid-feedback">{{ state_id }}</div>
            </div>

            <div class="form-group col-md-6">
              <label for="category">LGA</label>
              <select
                v-model="city_id"
                class="custom-select form__select"
                :class="[{ 'is-invalid': errors.city_id}]"
                :disabled="!state_id"
                id="city"
              >
                <option disabled value>Select LGA</option>
                <template v-if="citiesLoaded && cities.length">
                  <option v-for="city in cities" :value="city.id" :key="city.id">{{ city.name }}</option>
                </template>
                <template v-else>
                  <option>Error loading cities</option>
                </template>
              </select>
              <div class="invalid-feedback">{{ city_id }}</div>
            </div>
          </div>

          <div class="btn__group btn__group--form">
            <button
              type="submit"
              v-bind="{'disabled': loading}"
              @click.prevent="registerUser"
              class="btn__primary btn__primary--main"
            >Sign Up</button>
          </div>
        </form>
        <div class="form__meta">
          <p>
            Already have an account?
            <router-link :to="{name: 'login'}" class="form__link" exact>Sign In</router-link>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import { getStates, getCities } from "../utils/api/auth";
import form from "../mixins/form";
import auth from "../utils/validations/auth";

export default {
  name: "LearnerRegistration",
  mixins: [form],
  data() {
    return {
      itemData: {},
      errors: {},
      loading: false,
      state_id: "",
      states: [],
      statesLoaded: false,
      loadingStates: true,
      city_id: "",
      cities: [],
      citiesLoaded: false,
      loadingCities: true
    };
  },
  created() {
    this.reset();
    this.getStates();
  },
  computed: {
    ...mapGetters(["getRegistrationStatus", "getStatus", "getErrorMessage"])
  },
  watch: {
    getRegistrationStatus(value) {
      if (value) {
        this.clearErrors();
        this.$router.push("/");
        return true;
      }
      return false;
    },
    loggedUser: (newValue, oldValue) => {
      if (newValue && newValue.length) {
        this.$router.push("/");
      }
    },
    getStatus(newValue, oldValue) {
      this.loading = newValue === "loading";
    },
    isLoggedIn: (newValue, oldValue) => {
      if (newValue) {
        this.$router.push("/");
      }
    },
    state_id() {
      this.getCities(this.state_id);
    }
  },
  methods: {
    ...mapActions(["register"]),
    ...mapMutations(["reset"]),
    registerUser() {
      this.itemData.state_id = this.state_id;
      this.itemData.city_id = this.city_id;
      this.validate(this.itemData, auth.create);
      if (Object.keys(this.issues).length > 0) {
        this.errors = this.issues;
        return false;
      }
      this.loading = true;
      this.register(this.itemData);
      return true;
    },
    getStates() {
      getStates()
        .then(response => {
          if (response.status === 200) {
            this.states = response.data.data;
            this.statesLoaded = true;
            this.loadingStates = false;
          }
        })
        .catch(error => {
          this.statesLoaded = false;
          this.loadingStates = false;
          return error;
        });
      return true;
    },
    getCities(id) {
      getCities(id)
        .then(response => {
          if (response.status === 200) {
            this.cities = response.data.data;
            this.citiesLoaded = true;
            this.loadingCities = false;
          }
        })
        .catch(error => {
          this.statesLoaded = false;
          this.loadingCities = false;
          return error;
        });
      return true;
    }
  }
};
</script>

<style lang="scss" scoped>
.btn__group--form {
  margin-top: 30px;
  text-align: center;
}
.btn__primary--main {
  width: 230px;
}
</style>
